import type {
  GuardianAlphaNodeCreateRequest,
  GuardianAlphaService,
} from '@/types'
import styles from './guardianAlphaConfig.module.css'


interface Props {
  service: GuardianAlphaService
  canManageRegistration: boolean
  saving: boolean
  showCreate: boolean
  selectedGuardianNodeId: string
  nodeForm: GuardianAlphaNodeCreateRequest
  onToggleCreate: () => void
  onNodeFormChange: (next: GuardianAlphaNodeCreateRequest) => void
  onCreateNode: () => void
  onSelectNode: (guardianNodeId: string) => void
}

function statusClass(status: string): string {
  if (status === 'succeeded') return styles.statusSucceeded
  if (status === 'failed') return styles.statusFailed
  if (status === 'applying' || status === 'running') return styles.statusBusy
  return styles.statusNeutral
}

export default function GuardianAlphaNodeSidebar({
  service,
  canManageRegistration,
  saving,
  showCreate,
  selectedGuardianNodeId,
  nodeForm,
  onToggleCreate,
  onNodeFormChange,
  onCreateNode,
  onSelectNode,
}: Props) {
  return (
    <aside className={`${styles.card} ${styles.sidebar}`}>
      <div className={styles.titleRow}>
        <div className={styles.cardTitle}>
          GuardianNodes ({service.guardian_nodes.length})
        </div>
        {canManageRegistration && (
          <button className={styles.smallButton} onClick={onToggleCreate}>
            {showCreate ? 'Cancel' : 'Add'}
          </button>
        )}
      </div>

      {showCreate && (
        <div className={styles.formStack}>
          <label className={styles.compactLabel}>
            <span>Logical node key</span>
            <input
              className={styles.input}
              value={nodeForm.logical_node_key}
              onChange={(event) => onNodeFormChange({
                ...nodeForm,
                logical_node_key: event.target.value,
              })}
              placeholder="alpha_01"
            />
          </label>
          <label className={styles.compactLabel}>
            <span>Display name</span>
            <input
              className={styles.input}
              value={nodeForm.display_name}
              onChange={(event) => onNodeFormChange({
                ...nodeForm,
                display_name: event.target.value,
              })}
              placeholder="Optional"
            />
          </label>
          <label className={styles.compactLabel}>
            <span>Config path</span>
            <input
              className={styles.input}
              value={nodeForm.config_path}
              onChange={(event) => onNodeFormChange({
                ...nodeForm,
                config_path: event.target.value,
              })}
              placeholder="/home/node/.../config.yml"
            />
          </label>
          <label className={styles.compactLabel}>
            <span>Working directory</span>
            <input
              className={styles.input}
              value={nodeForm.working_directory}
              onChange={(event) => onNodeFormChange({
                ...nodeForm,
                working_directory: event.target.value,
              })}
              placeholder="/home/node/..."
            />
          </label>
          <button
            className={styles.primaryButton}
            onClick={onCreateNode}
            disabled={
              saving
              || !nodeForm.logical_node_key
              || !nodeForm.config_path
              || !nodeForm.working_directory
            }
          >
            Create
          </button>
        </div>
      )}

      {service.guardian_nodes.length === 0 && (
        <p className={styles.description}>
          등록된 GuardianNode가 없어 Restart할 수 없습니다.
        </p>
      )}

      {service.guardian_nodes.map((item) => (
        <button
          key={item.id}
          className={[
            styles.guardianNodeButton,
            item.id === selectedGuardianNodeId
              ? styles.guardianNodeButtonSelected
              : '',
          ].filter(Boolean).join(' ')}
          onClick={() => onSelectNode(item.id)}
        >
          <strong>{item.display_name}</strong>
          <span className={`${styles.nodeStatus} ${statusClass(item.last_apply_status)}`}>
            {item.last_apply_status}
          </span>
        </button>
      ))}
    </aside>
  )
}
